import { capitalize, orderBy, uniq } from 'lodash';
import type { FfuBaselineChange } from './ffu-baseline.helpers';
import { isElementRequirement } from './building-part-requirement.helpers';
import { assertUnreachable } from './function.helpers';
import type { RescuedStorey } from './design/design-rescue.helpers';
import type { FfuPartMerge } from './ffu-final-output.helpers';
import {
  byFfuStoreyIndex,
  hasFfuFootprint,
  isRoofOnlyAttic,
} from './design/design-from-ffu.helpers';
import { getStoreyGfaExcludingCutouts } from './design/cutout.helpers';
import { getProgramGfaAreas } from './design/program-gfa.helpers';
import { getActivityTypeLabel } from './design/activities.helpers';
import { labelFromValue } from './string.helpers';
import { ActivityType } from '../models/design/activities/activity-type.model';
import type { ActivityMap } from '../models/design/activities/activity.model';
import { RESIDENTIAL_PROGRAM_TYPE_LABELS } from '../models/design/activities/properties/program-type.model';
import type {
  EXTRACTED_BUILDING_CONNECTION_RELATIONSHIPS,
  RoofFormType,
} from '../models/extracted-design-facts.model';
import type { BuildingDesign } from '../models/design/building-design.model';
import type {
  FfuBuilding,
  FfuOutputFacts,
  FfuSitePlacement,
  FfuSitePrecision,
  FfuStorey,
} from '../models/ffu-output.model';
import {
  BUILDING_PART_MATERIAL_LABELS,
  type BuildingPartLimitComparison,
  type BuildingPartRangeParameterId,
  type BuildingPartRequirementAggregated,
  type ElementRequirement,
  type LegacyBuildingPartRequirement,
} from '../models/building-part-requirement.model';
import type {
  ProjectFileBatchBuildingComposition,
  ProjectFileBatchConsolidatedBuilding,
} from '../models/project-file-batch.model';

/** A printed area this far from the traced one is worth checking. */
const AREA_CHECK_SHARE = 0.1;

/** Section headings of the message, in order; choices come last. */
export const FFU_MESSAGE_HEADINGS = {
  overview: 'Overview',
  requirements: 'Requirements',
  choices: 'Assumptions & decisions',
} as const;

export type FfuBaselineSummaryInput = {
  facts: FfuOutputFacts;
  /**
   * The batch's requirement list. Its whole-building caps are summarised;
   * element rows are read per building from {@link FfuOutputFacts}.
   */
  requirements: readonly BuildingPartRequirementAggregated[];
  /** Buildings the merge joined, from the batch's aggregated output. */
  compositions: readonly ProjectFileBatchBuildingComposition[];
  /** The merge's buildings, to name the joined ones by their labels. */
  consolidatedBuildings: readonly ProjectFileBatchConsolidatedBuilding[];
  /** The design as saved on the new project. */
  design: BuildingDesign;
  changes: readonly FfuBaselineChange[];
  /** The roof area the saved project computed, when it has a roof. */
  roofArea: number | null;
  fileCount: number;
  /** Where the FFU's site was placed on the map; null when it was not found. */
  placement: FfuSitePlacement | null;
  /** Storeys whose drawn shape the editor rejected and that got a simpler one. */
  rescued?: readonly RescuedStorey[];
  /** Buildings described in parts that the final output made one. */
  partMerges?: readonly FfuPartMerge[];
};

/** A number as the message gives it: "7,118", "3.15". */
export const ffuNumber = (value: number): string =>
  value.toLocaleString('en-US', { maximumFractionDigits: 2 });

/** A share as the message gives it: 0.55 is "55 %". */
export const ffuPercent = (share: number): string =>
  `${Math.round(share * 100)} %`;

const m2 = (value: number): string => `${ffuNumber(Math.round(value))} m²`;

const changeNumber = (value: number | string): string =>
  typeof value === 'number' ? ffuNumber(value) : value;

/** Printed words this short, or with digits, are designations: "A", "B2", "KV". */
const isDesignation = (word: string): boolean =>
  word.length < 3 || /[^\p{L}]/u.test(word);

/**
 * A building's name as people say it: the part after a block prefix
 * ("KV H - "), and when printed in capitals, each word capitalised except
 * designations. "KV H - HJÄLPSLAKTAREN 1-7-8" is "Hjälpslaktaren 1-7-8",
 * "HUS A" is "Hus A".
 */
export const shortBuildingName = (name: string): string => {
  const last = name.split(' - ').at(-1)?.trim() || name;
  if (last !== last.toUpperCase() || last === last.toLowerCase()) return last;
  return last
    .split(' ')
    .map((word) => (isDesignation(word) ? word : capitalize(word)))
    .join(' ');
};

/**
 * {@link name} short, or in full when another of {@link names} has the same
 * short name: "KV H - HUS A" and "KV I - HUS A" are both "Hus A" otherwise.
 */
const distinctBuildingName = (
  name: string,
  names: readonly (string | null | undefined)[],
): string => {
  const short = shortBuildingName(name);
  return names.some(
    (other) => other && other !== name && shortBuildingName(other) === short,
  )
    ? name
    : short;
};

/**
 * Every building name the summary can print: the design's and the FFU's, so
 * a building no drawing showed keeps its name apart too.
 */
const buildingNames = (
  design: BuildingDesign,
  facts: FfuOutputFacts,
): (string | null | undefined)[] => [
  ...design.buildings.map((building) => building.name),
  ...facts.buildings.map((building) => building.buildingName),
];

/** A design building's short name, or its place in the design. */
const designBuildingName = (
  building: BuildingDesign['buildings'][number],
  buildingIndex: number,
  names: readonly (string | null | undefined)[],
): string =>
  building.name
    ? distinctBuildingName(building.name, names)
    : `Building ${buildingIndex + 1}`;

const ffuBuildingName = (
  building: FfuBuilding,
  buildings: readonly FfuBuilding[],
): string =>
  building.buildingName
    ? distinctBuildingName(
        building.buildingName,
        buildings.map((item) => item.buildingName),
      )
    : 'A building';

const files = (count: number): string =>
  count === 1 ? '1 file' : `${count} files`;

const storeys = (count: number): string =>
  count === 1 ? '1 storey' : `${count} storeys`;

/** "a", "a and b", "a, b and c". */
const listText = (items: readonly string[]): string =>
  items.length < 2
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;

const section = (title: string, lines: readonly string[]): string =>
  [`### ${title}`, ...lines.map((line) => `- ${line}`)].join('\n');

/** How each roof form reads in a sentence. */
const ROOF_FORM_LABELS: Record<RoofFormType, string> = {
  flat: 'flat',
  sadel: 'gable',
  pulpet: 'mono-pitch',
  mansard: 'mansard',
};

/** What the design is used for, largest first, in lower case. */
const programNames = (design: BuildingDesign, total: number): string[] =>
  orderBy(
    getProgramGfaAreas(design, total).filter(
      (program) =>
        program.type !== ActivityType.Other && Math.round(program.area) > 0,
    ),
    'area',
    'desc',
  ).map((program) => getActivityTypeLabel(program.type).toLowerCase());

/** Whether a roof pitch change is a default or an estimate, not a read. */
const isPitchAssumed = (
  change: FfuBaselineChange,
  facts: FfuOutputFacts,
): boolean =>
  change.from === 'roof.form' ||
  (change.from === 'roof.pitchDeg' &&
    facts.buildings[0]?.geometry.roof?.pitchRead?.from !== 'direct');

/** The choice a change made, with its reason. Null when the documents gave the value. */
const assumedLine = (
  change: FfuBaselineChange,
  facts: FfuOutputFacts,
  roofArea: number | null,
  pitchAssumed: boolean,
): string | null => {
  const roof = facts.buildings[0]?.geometry.roof ?? null;
  switch (change.from) {
    case 'window_ratio':
      return facts.buildings[0]?.properties.window_ratio?.determined_by ===
        'estimated'
        ? `**Window-to-wall ratio:** not stated in the documents, so ${ffuPercent(Number(change.value))} was estimated from the drawings.`
        : null;
    case 'roof.pitchDeg':
      return isPitchAssumed(change, facts)
        ? `**Roof pitch:** not stated in the documents, so ${changeNumber(change.value)}° was estimated from the drawings.`
        : null;
    case 'roof.form':
      return `**Roof pitch:** not stated in the documents, so ${changeNumber(change.value)}° was assumed, usual for a ${
        roof ? ROOF_FORM_LABELS[roof.form] : 'this'
      } roof.`;
    case 'roof.area':
      return roofArea !== null && pitchAssumed
        ? `**Roof area:** ${m2(roofArea)}, computed from the outlines and the assumed roof pitch.`
        : null;
    default:
      return assertUnreachable(change.from);
  }
};

/** Element row places as a sentence names them; other places by their own label. */
const PLACE_LABELS: Record<string, string> = {
  facade: 'Facade',
  facade_cladding: 'Facade cladding',
  external_wall: 'External wall',
  roof: 'Roof',
  floor_slab: 'Floor slabs',
  frame: 'Frame',
};

const placeLabel = (place: string): string =>
  PLACE_LABELS[place] ?? labelFromValue(place);

const RANGE_LABELS: Record<
  BuildingPartRangeParameterId,
  { label: string; unit: string }
> = {
  thermal_transmittance: { label: 'U-value', unit: ' W/m²K' },
  building_height: { label: 'Building height', unit: ' m' },
  storey_count: { label: 'Storeys', unit: '' },
};

const COMPARISON_SIGNS: Record<BuildingPartLimitComparison, string> = {
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
  eq: '=',
};

const materialText = (row: ElementRequirement): string =>
  row.share === null || row.share === 1
    ? row.name
    : `${row.name} (${ffuPercent(row.share)})`;

/** One line per building part with its materials; buildings named when there are several. */
const elementLines = (facts: FfuOutputFacts): string[] =>
  facts.buildings.flatMap((building) => {
    const rows: ElementRequirement[] = building.requirements.flatMap((row) =>
      isElementRequirement(row) ? [row] : [],
    );
    return uniq(rows.map((row) => row.place)).map((place) => {
      const part = placeLabel(place);
      const label =
        facts.buildings.length > 1
          ? `${ffuBuildingName(building, facts.buildings)}, ${part.toLowerCase()}`
          : part;
      const materials = rows
        .filter((row) => row.place === place)
        .map(materialText);
      return `**${label}:** ${listText(materials)}`;
    });
  });

/** A whole-building cap or a leftover row. Null when it states no value. */
const capLine = (row: LegacyBuildingPartRequirement): string | null => {
  const scope = row.name === null ? '' : ` (${row.name})`;
  const { constraint } = row;
  switch (constraint.kind) {
    case 'range': {
      const { label, unit } = RANGE_LABELS[constraint.parameter_id];
      const bounds = [
        constraint.min === null
          ? null
          : `≥ ${ffuNumber(constraint.min)}${unit}`,
        constraint.max === null
          ? null
          : `≤ ${ffuNumber(constraint.max)}${unit}`,
      ].filter((bound): bound is string => bound !== null);
      if (bounds.length === 0) return null;
      return `**${label}${scope}:** ${bounds.join(', ')}`;
    }
    case 'limit':
      return `**${row.source_label}${scope}:** ${COMPARISON_SIGNS[constraint.comparison]} ${ffuNumber(constraint.number)} ${constraint.unit}`;
    case 'text':
      return `**${row.source_label}${scope}:** ${constraint.text}`;
    case 'material': {
      const named = constraint.allowed.map(
        (id) => BUILDING_PART_MATERIAL_LABELS[id],
      );
      const material = row.material ?? listText(named);
      return material === '' ? null : `**${row.source_label}:** ${material}`;
    }
    default:
      return assertUnreachable(constraint);
  }
};

const capLines = (
  requirements: readonly BuildingPartRequirementAggregated[],
): string[] =>
  requirements.flatMap((row) => {
    if (isElementRequirement(row)) return [];
    const line = capLine(row);
    return line === null ? [] : [line];
  });

const joinedCompositions = (
  compositions: readonly ProjectFileBatchBuildingComposition[],
): ProjectFileBatchBuildingComposition[] =>
  compositions.filter((composition) => composition.shouldModelAsSingleBuilding);

type ConnectionRelationship =
  (typeof EXTRACTED_BUILDING_CONNECTION_RELATIONSHIPS)[number];

/** Why the merge modelled buildings as one, in a clause. */
const COMPOSITION_REASONS: Record<ConnectionRelationship, string> = {
  shared_base_slab: 'they stand on a shared base slab',
  connected_volume: 'the documents show them connected',
  separate: 'the documents treat them as one',
  unknown: 'the documents treat them as one',
};

/** Why the merge modelled buildings as one; the overview names them. */
const compositionLines = (
  compositions: readonly ProjectFileBatchBuildingComposition[],
): string[] =>
  joinedCompositions(compositions).map(
    ({ relationship }) =>
      `**Buildings:** ${COMPOSITION_REASONS[relationship]}, so they were modelled as one.`,
  );

/**
 * How a building described in parts was modelled, so the user knows why its
 * storey heights may not match each part's drawings.
 */
const partMergeLines = (
  partMerges: readonly FfuPartMerge[],
  design: BuildingDesign,
  drawn: readonly FfuBuilding[],
  names: readonly (string | null | undefined)[],
): string[] =>
  partMerges.map(({ keys, buildingName, partNames, averagedHeights }) => {
    const index = drawn.findIndex((building) =>
      [building.id, ...building.instanceKeys].some(
        (key) => key !== null && keys.includes(key),
      ),
    );
    const building = design.buildings[index];
    const name =
      building === undefined
        ? (buildingName ?? 'A building')
        : designBuildingName(building, index, names);
    return `**${name}:** modelled as one building from ${listText(partNames)}${
      averagedHeights
        ? '; where a storey has different heights in the parts, it has their mean, so storey heights can differ from the drawings'
        : ''
    }.`;
  });

/** Why a storey's shape is uncertain, most severe first. */
const SHAPE_DOUBTS = ['noOutline', 'rescued', 'areaOff'] as const;
type ShapeDoubt = (typeof SHAPE_DOUBTS)[number];

const SHAPE_DOUBT_TEXT: Record<ShapeDoubt, (storeys: string) => string> = {
  noOutline: (storeys) =>
    `no drawing showed the outline of ${storeys}, so it has no shape.`,
  rescued: (storeys) =>
    `the drawn outline of ${storeys} could not be used, so a simpler shape was used.`,
  areaOff: (storeys) =>
    `the drawn outline of ${storeys} differs by more than ${ffuPercent(AREA_CHECK_SHARE)} from the area the documents print; the drawn outline was kept.`,
};

/** One line per reason a storey of the building has an uncertain shape. */
const shapeDoubtLines = (
  name: string,
  building: BuildingDesign['buildings'][number],
  buildingIndex: number,
  areas: readonly number[],
  stated: readonly FfuStorey[],
  rescued: readonly RescuedStorey[],
): string[] => {
  const doubtOf = (storeyIndex: number): ShapeDoubt | null => {
    const area = areas[storeyIndex] ?? 0;
    const printed = stated[storeyIndex]?.gfa ?? null;
    if (area === 0) return 'noOutline';
    const wasRescued = rescued.some(
      (row) =>
        row.buildingIndex === buildingIndex && row.storeyIndex === storeyIndex,
    );
    if (wasRescued) return 'rescued';
    if (printed === null) return null;
    return Math.abs(printed - area) > printed * AREA_CHECK_SHARE
      ? 'areaOff'
      : null;
  };
  const doubts = building.storeys.map((storey, storeyIndex) => ({
    name: storey.name ?? `Storey ${storeyIndex + 1}`,
    doubt: doubtOf(storeyIndex),
  }));
  return SHAPE_DOUBTS.flatMap((doubt) => {
    const names = doubts
      .filter((row) => row.doubt === doubt)
      .map((row) => row.name);
    return names.length === 0
      ? []
      : [`**${name}:** ${SHAPE_DOUBT_TEXT[doubt](listText(names))}`];
  });
};

/** How buildings hang together, as a sentence says it. Null when unknown. */
const CONNECTION_TEXT: Record<ConnectionRelationship, string | null> = {
  shared_base_slab: 'on a shared base slab',
  connected_volume: 'connected',
  separate: 'separate',
  unknown: null,
};

type ResidentialActivity = ActivityMap[ActivityType.Residential];

const isResidential = (
  activity: NonNullable<BuildingDesign['activities']>[number],
): activity is ResidentialActivity =>
  activity.type === ActivityType.Residential;

/** "Hus A: x; Hus B: y" for several buildings, the bare text for one. Null when none has a text. */
const perBuilding = (
  rows: readonly { name: string; text: string | null }[],
): string | null => {
  const stated = rows.flatMap(({ name, text }) =>
    text === null ? [] : [{ name, text }],
  );
  if (stated.length === 0) return null;
  if (rows.length === 1) return stated[0]?.text ?? null;
  return stated.map(({ name, text }) => `${name}: ${text}`).join('; ');
};

const roofText = (building: FfuBuilding): string | null => {
  const roof = building.geometry.roof;
  const pitch =
    roof?.form !== 'flat' &&
    roof?.pitchDeg != null &&
    roof.pitchRead?.from === 'direct'
      ? `${ffuNumber(roof.pitchDeg)}°`
      : null;
  const parts = [roof ? ROOF_FORM_LABELS[roof.form] : null, pitch].filter(
    (part): part is string => part !== null,
  );
  return parts.length === 0 ? null : parts.join(', ');
};

const buildingsText = (
  design: BuildingDesign,
  drawn: readonly FfuBuilding[],
  compositions: readonly ProjectFileBatchBuildingComposition[],
  consolidated: readonly ProjectFileBatchConsolidatedBuilding[],
  names: readonly (string | null | undefined)[],
): string => {
  const labels = consolidated.map((building) => building.buildingLabel);
  const shortLabel = (key: string): string => {
    const label = consolidated.find(
      (building) => building.buildingInstanceKey === key,
    )?.buildingLabel;
    return label ? distinctBuildingName(label, labels) : labelFromValue(key);
  };
  const joined = joinedCompositions(compositions);
  if (joined.length > 0) {
    return joined
      .map((composition) => {
        const relation = CONNECTION_TEXT[composition.relationship];
        return `${listText(composition.composingBuildingKeys.map(shortLabel))}, ${
          relation === null ? '' : `${relation} and `
        }modelled as one building`;
      })
      .join('; ');
  }
  return listText(
    design.buildings.map((building, buildingIndex) => {
      const name = designBuildingName(building, buildingIndex, names);
      const relationship = drawn[buildingIndex]?.connection?.relationship;
      const relation = relationship ? CONNECTION_TEXT[relationship] : null;
      return relation === null ? name : `${name} (${relation})`;
    }),
  );
};

/**
 * The project at a glance, one line per subject the project has a value
 * for: building type, location, buildings, GFA and storeys, apartments,
 * garage and roof form. Materials are requirements.
 */
const overviewLines = ({
  facts,
  design,
  drawn,
  compositions,
  consolidatedBuildings,
}: {
  facts: FfuOutputFacts;
  design: BuildingDesign;
  drawn: readonly FfuBuilding[];
  compositions: readonly ProjectFileBatchBuildingComposition[];
  consolidatedBuildings: readonly ProjectFileBatchConsolidatedBuilding[];
}): string[] => {
  const allNames = buildingNames(design, facts);
  const names = design.buildings.map((building, buildingIndex) =>
    designBuildingName(building, buildingIndex, allNames),
  );
  const totals = design.buildings.map((building) =>
    building.storeys
      .map(getStoreyGfaExcludingCutouts)
      .reduce((sum, area) => sum + area, 0),
  );
  const total = totals.reduce((sum, area) => sum + area, 0);
  const residential = design.activities?.find(isResidential);
  const programType = residential?.properties.program_type;
  const uses = programNames(design, total).map((use) =>
    use === 'residential' && programType
      ? `${RESIDENTIAL_PROGRAM_TYPE_LABELS[programType].toLowerCase()} residential`
      : use,
  );
  const garage =
    getProgramGfaAreas(design, total).find(
      (program) => program.type === ActivityType.Garage,
    )?.area ?? 0;
  const apartments = drawn.flatMap((building) =>
    building.apartmentCount == null ? [] : [building.apartmentCount],
  );
  const apartmentCount = apartments.reduce((sum, count) => sum + count, 0);
  const livingArea = residential?.properties.living_area_per_apartment;
  const gfaAndStoreys = perBuilding(
    design.buildings.map((building, buildingIndex) => {
      const area = totals[buildingIndex] ?? 0;
      const below =
        drawn[buildingIndex]?.geometry.storeys.filter(
          (storey) => storey.index !== null && storey.index < 0,
        ).length ?? 0;
      const total = building.storeys.length;
      const count =
        below > 0
          ? `${storeys(total)}, ${total - below} above and ${below} below ground`
          : storeys(total);
      return {
        name: names[buildingIndex] ?? '',
        text: Math.round(area) > 0 ? `${m2(area)}, ${count}` : count,
      };
    }),
  );
  const drawnText = (text: (building: FfuBuilding) => string | null) =>
    perBuilding(
      names.map((name, buildingIndex) => {
        const building = drawn[buildingIndex];
        return { name, text: building ? text(building) : null };
      }),
    );
  const lines: [string, string | null][] = [
    ['Building type', uses.length > 0 ? listText(uses) : null],
    ['Location', facts.location],
    [
      design.buildings.length === 1 &&
      joinedCompositions(compositions).length === 0
        ? 'Building'
        : 'Buildings',
      buildingsText(
        design,
        drawn,
        compositions,
        consolidatedBuildings,
        allNames,
      ),
    ],
    ['GFA and storeys', gfaAndStoreys],
    [
      'Apartments',
      apartments.length === 0
        ? null
        : `${ffuNumber(apartmentCount)}${
            livingArea ? `, average living area ${m2(livingArea)}` : ''
          }`,
    ],
    ['Garage', Math.round(garage) > 0 ? `${m2(garage)} GFA` : null],
    ['Roof', drawnText(roofText)],
  ];
  return lines.flatMap(([subject, text]) =>
    text ? [`**${subject}:** ${text}`] : [],
  );
};

/**
 * The facts of the first agent chat message in a project made from an FFU,
 * as Markdown lines: the opening line, the project at a glance, the
 * requirements the overview does not give, and every choice the import made
 * where the documents were unclear, each with its reason. Each value is in
 * one line only, and subjects without a value have no line.
 */
export type FfuBaselineSummary = {
  /**
   * The project as it was created, building by building and storey by
   * storey, for the model to check the other lines against.
   */
  project: string[];
  opening: string;
  overview: string[];
  requirements: string[];
  choices: string[];
  /**
   * The documents' own words, often Swedish, on why buildings were modelled
   * as one: for the model to paraphrase, never for the user to read as is.
   */
  compositionNotes: string[];
};

/**
 * Each building of the design with its storeys from the bottom up, numbered
 * from 1 as the design tab numbers them: name, area and height.
 */
const projectLines = (
  design: BuildingDesign,
  drawn: readonly FfuBuilding[],
  names: readonly (string | null | undefined)[],
): string[] =>
  design.buildings.flatMap((building, buildingIndex) => {
    const below =
      drawn[buildingIndex]?.geometry.storeys.filter(
        (storey) => storey.index !== null && storey.index < 0,
      ).length ?? 0;
    const count = building.storeys.length;
    return [
      `${designBuildingName(building, buildingIndex, names)}: ${storeys(count)}, ${count - below} above and ${below} below ground`,
      ...building.storeys.map(
        (storey, storeyIndex) =>
          `  - storey ${storeyIndex + 1}${storey.name ? ` "${storey.name}"` : ''}: ${m2(getStoreyGfaExcludingCutouts(storey))}, ${ffuNumber(storey.height)} m high`,
      ),
    ];
  });

/** The coarse levels a placement names in the message, by precision. */
const SITE_LEVELS: Partial<Record<FfuSitePrecision, string>> = {
  street: 'street',
  district: 'district',
  locality: 'town',
  municipality: 'municipality',
  region: 'county',
};

/**
 * What the message says about the site: nothing when the plot, an address or
 * the block placed it, else what the project was placed on instead.
 */
export const siteLine = (placement: FfuSitePlacement | null): string | null => {
  if (placement === null) {
    return '**Site:** the location in the documents could not be found on the map, so the project is not placed on it.';
  }
  const { precision, placeName } = placement;
  if (precision === 'plot' || precision === 'address' || precision === 'block')
    return null;
  const level = precision === null ? undefined : SITE_LEVELS[precision];
  const place =
    placeName === null
      ? level === undefined
        ? 'the closest place the search found'
        : `the ${level}`
      : level === undefined
        ? placeName
        : `${placeName} (${level})`;
  return `**Site:** the documents do not show exactly where the plot is, so the project is placed on ${place}.`;
};

/**
 * {@link FfuBaselineSummary} for a new project. Built from the saved design
 * and the changes, so it says what the project holds.
 */
export const ffuBaselineSummary = ({
  facts,
  requirements,
  compositions,
  consolidatedBuildings,
  design,
  changes,
  roofArea,
  fileCount,
  placement,
  rescued = [],
  partMerges = [],
}: FfuBaselineSummaryInput): FfuBaselineSummary => {
  const drawn = facts.buildings.filter(hasFfuFootprint);
  const names = buildingNames(design, facts);
  const checks = design.buildings.flatMap((building, buildingIndex) => {
    const name = designBuildingName(building, buildingIndex, names);
    const storeys = [...(drawn[buildingIndex]?.geometry.storeys ?? [])].sort(
      byFfuStoreyIndex,
    );
    // The design leaves these attics out, so the rest line up with its storeys.
    const roofOnly = storeys.filter(isRoofOnlyAttic);
    return [
      ...shapeDoubtLines(
        name,
        building,
        buildingIndex,
        building.storeys.map(getStoreyGfaExcludingCutouts),
        storeys.filter((storey) => !isRoofOnlyAttic(storey)),
        rescued,
      ),
      ...(roofOnly.length === 0
        ? []
        : [
            `**${name}:** no plan or area of its own was found for ${listText(
              roofOnly.map((storey) => storey.name ?? 'the attic'),
            )}, so it was left out as roof.`,
          ]),
    ];
  });
  facts.buildings
    .filter((building) => !hasFfuFootprint(building))
    .forEach((building) => {
      checks.push(
        `**${ffuBuildingName(building, facts.buildings)}:** no drawing showed its shape, so it is not in the project.`,
      );
    });
  const site = siteLine(placement);
  if (site !== null) checks.push(site);

  // The roof area is only as certain as the pitch it is tilted by.
  const pitchAssumed = changes.some((change) => isPitchAssumed(change, facts));
  const assumed = changes.flatMap((change) => {
    const line = assumedLine(change, facts, roofArea, pitchAssumed);
    return line === null ? [] : [line];
  });
  const project = facts.name === null ? 'this project' : `**${facts.name}**`;
  return {
    project: projectLines(design, drawn, names),
    opening: `I created ${project} from ${files(fileCount)}.`,
    overview: overviewLines({
      facts,
      design,
      drawn,
      compositions,
      consolidatedBuildings,
    }),
    requirements: [...elementLines(facts), ...capLines(requirements)],
    choices: [
      ...compositionLines(compositions),
      ...partMergeLines(partMerges, design, drawn, names),
      ...checks,
      ...assumed,
    ],
    compositionNotes: joinedCompositions(compositions).map(
      ({ compositionNote }) => compositionNote,
    ),
  };
};

/**
 * The chat message as code writes it when the model's message fails: the
 * opening line, then each {@link FFU_MESSAGE_HEADINGS} section that has
 * lines, choices last.
 */
export const ffuChatMessageInCode = ({
  opening,
  overview,
  requirements,
  choices,
}: FfuBaselineSummary): string =>
  [
    opening,
    ...(
      [
        [FFU_MESSAGE_HEADINGS.overview, overview],
        [FFU_MESSAGE_HEADINGS.requirements, requirements],
        [FFU_MESSAGE_HEADINGS.choices, choices],
      ] as const
    ).flatMap(([title, lines]) =>
      lines.length === 0 ? [] : [section(title, lines)],
    ),
  ].join('\n\n');
